import React, { useEffect, useState } from "react";

import {
  Autocomplete,
  Button,
  Checkbox,
  DocumentListItem,
  ExpandablePanel,
  ExpandablePanelBody,
  ExpandablePanelHeader,
  InfoPanel,
  Modal,
  Select,
  SelectEvent,
  TextArea,
  TextField,
  Typography,
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as UploadIcon } from "@king-ict/eupisi-ui/components/Icon/Upload.svg";
import { useValidation } from "@king-ict/eupisi-ui/hooks";
import { CodeBook, DocumentData } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { saveAs } from "file-saver";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import {
  createProgramRequest,
  DocumentUploadRequest,
  downloadDocument,
  editProgramRequest,
  getActiveProgramTypes,
  getAndCheckInstitutionRequest,
  getProgramRequestDetail,
  uploadDocument
} from "../../api/nisooApi";
import { DocumentsUploadModal } from "../../components/DocumentsUploadModal";
import InstitutionBasicInfo from "../../components/InstitutionBasicInfo";
import OtherData from "../../components/OtherData";
import StaticField from "../../components/StaticField";
import UploadButton from "../../components/UploadButton";
import { useInput, useSubmitLock } from "../../hooks";
import { DocumentType } from "../../models/Document";
import { Institution, InstitutionLocation, LocationState } from "../../models/Institution";
import {
  ProgramType, ProgramTypeCodeBook,
} from "../../models/Program";
import type { ProgramCodeBook } from "../../models/Program";
import {
  Competence,
  convertFromProgramRequest,
  // NISOO2 createOtherLegalEntityLocation,
  createProgramRequestData,
  createProgramRequestTeacherInitial,
  mapProgramRequestData,
  OtherEnrollmentConditionId,
  OtherFormOfTeachingId,
  OtherKnowledgeCheckId,
  // NISOO2OtherLegalEntityLocation,
  ProgramRequestData,
  ProgramRequestDataValidationScheme,
  ProgramRequestDetail,
  ProgramRequestType,
} from "../../models/ProgramRequest";
import { AdditionalDocumentation } from "../../models/ProgramRequest/AdditionalDocumentation";
import { AdditionalDocumentationType } from "../../models/ProgramRequest/AdditionalDocumentationType";
import { createCompetenceInitial } from "../../models/ProgramRequest/Competence";
import { Module } from "../../models/ProgramRequest/Module";
import { OtherLegalEntityLocation } from "../../models/ProgramRequest/OtherLegalEntityLocation";
import { ProgramRequestStatus } from "../../models/ProgramRequest/ProgramRequestStatus";
import { ProgramRequestTeacher } from "../../models/ProgramRequest/ProgramRequestTeacher";
import { TeacherLanguage } from "../../models/ProgramRequest/TeacherLanguage";
import { RoleEnum } from "../../models/User/RoleEnum";
import {
  useGetEnrollmentConditionsQuery,
  useGetFormsOfTeachingQuery,
  useGetKnowledgeChecksQuery,
  useGetProgramsByTypeQuery
} from "../../queries/codeBookQuery";
import { useGetUserSessionQuery } from "../../queries/userQuery";
import { dashboardRoute, programRequestEditUrl } from "../../routes/routes";
import { isMinisterProgramRequest } from "../../utils/programRequestUtils";
import InstitutionPanel from "../Institution/InstitutionPanel";
import { ProgramRequestModulePanel } from "../ProgramRequest/ProgramRequestModulePanel";
import { canEditMinisterMzomAdditionalDocumentation } from "../ProgramRequest/utils/permissionGuards";
import { CompetenceInfo } from "./CompetenceInfo";
import { ReadOnlyCompetencesTable } from "./components/ReadOnlyCompetencesTable";
import { ConditionalBasicInfo } from "./ConditionalBasicInfo";
import { OtherEnrollmentConditionsInfo } from "./OtherEnrollmentConditionsInfo";
import { OtherLegalEntityInfos } from "./OtherLegalEntityInfos";
import styles from "./ProgramRequestEdit.module.scss";
import TeacherInfo from "./TeacherInfo";
import { institutionLocationsToCodeBook } from "./utils/locationUtils";
import { buildMinisterReadonlyRequestPatch } from "./utils/ministerReadonly";
import { isProgramTypeWithModuleSection, isProgramTypeWithoutCompetenceSection } from "./utils/programTypeGuards";

function syncOtherArray(
  ids: number[],
  otherValues: string[] | undefined,
  otherId: number
): string[] {
  if (!ids.includes(otherId)) {
    return [];
  }

  const currentValues = otherValues ?? [];

  if (!currentValues.length) {
    return [""];
  }

  return currentValues;
}

export function ProgramRequestEdit() {
  const [programRequest, setProgramRequest] = useState<ProgramRequestData>(createProgramRequestData());
  const [programRequestDetail, setProgramRequestDetail] = useState<ProgramRequestDetail>();
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [openConfirmAndSendModal, setOpenConfirmAndSendModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const { isSubmitting: isSaving, runExclusive } = useSubmitLock();
  const [chackValidationError, setChackValidationError] = useState(false);
  const [openUploadAdditionalDocumentationModal, setOpenUploadAdditionalDocumentationModal] = useState(false);

  const { t } = useTranslation();
  const params = useParams<{ programRequestId: string }>();
  const history = useHistory();

  const [institutionData, setInstitutionData] = useState<Institution>();
  const [programTypes, setProgramTypes] = useState<ProgramTypeCodeBook[]>([]);
  const { data: userSession } = useGetUserSessionQuery();
  const { data: formsOfTeaching } = useGetFormsOfTeachingQuery();
  const { data: knowledgeChecks } = useGetKnowledgeChecksQuery();
  const { data: enrollmentConditions } = useGetEnrollmentConditionsQuery();
  const { data: programs } = useGetProgramsByTypeQuery(programRequest.programTypeId!, {
    skip: !programRequest.programTypeId,
  });

  const { handleChange } = useInput();

  const [errors, doValidations] = useValidation({
    data: programRequest,
    validationSchema: ProgramRequestDataValidationScheme,
  });

  const [isSendRequest, setIsSendRequest] = useState<boolean>(false);
  const [currentAdditionalDocumentationType, setCurrentAdditionalDocumentationType] = useState<AdditionalDocumentationType>();

  useEffect(() => {
    const getAndCheckInstitution = async () => {
      if (!userSession) {
        return;
      }

      const result = await getAndCheckInstitutionRequest(userSession.institutionId!);
      setInstitutionData(result);
    }
    getAndCheckInstitution();
  }, [userSession])

  useEffect(() => {
    const getProgramTypes = async (date?: Date) => {
      const response = await getActiveProgramTypes(date);
      setProgramTypes(response);
    }
    const getProgramRequestAsync = async () => {
      if (!params.programRequestId || isNaN(parseInt(params.programRequestId))) {
        await getProgramTypes();
        return;
      }
      const parsedProgramRequestId = parseInt(params.programRequestId);
      // const results = await getProgramRequest(parsedProgramRequestId);
      const results = await getProgramRequestDetail(parsedProgramRequestId);

      setProgramRequestDetail(results.programRequestDetail);

      // const programRequest = convertApiResponseToViewModel(results);
      const programRequest = mapProgramRequestData(results, results.programRequestDetail);
      //if (programRequestUnlocked.statusId !== ProgramRequestStatus.UnlockedASOO) {
      //  history.push(startRoute());
      //}
      setProgramRequest(programRequest);

      await getProgramTypes(programRequest.requestDate);
    }
    getProgramRequestAsync();



  }, [params.programRequestId, history])

  const fillLocations = () => {
    if (!institutionData || !institutionData.locations) {
      return codeBookToSelectData(undefined);
    } else {
      const locations: CodeBook[] = institutionLocationsToCodeBook(
        institutionData.locations.filter(
          (x: InstitutionLocation) =>
            x.activityState === LocationState.Active ||
            (x.deactivateDate && new Date(x.deactivateDate) > new Date()),
        ),
      );
      return codeBookToSelectData(locations);
    }
  };

  // NOTE: makeIds was unused; removed during refactor.

  const getProgramsSelectData = () => {
    const isMinisterProgramType = isMinisterProgramRequest(programRequest.programTypeId);
    const isActiveProgram = (program: ProgramCodeBook) => {
      const dateTo = program.dateTo;
      if (!dateTo) {
        return true;
      }
      const parsed = dateTo instanceof Date ? dateTo : new Date(dateTo);
      return !Number.isNaN(parsed.getTime()) && parsed > new Date();
    };

    if (!programs) {
      return codeBookToSelectData(undefined);
    } else if (isMinisterProgramType) {
      const ministerPrograms = programs
        .filter((p: { programTypeId: number; programWithMinisterResolution?: boolean }) => p.programTypeId === ProgramType.ProgramWithMinisterResolution || !!p.programWithMinisterResolution)
        .filter(isActiveProgram);

      return codeBookToSelectData(ministerPrograms);
    } else if (
      programRequest.programTypeId &&
      (
        programRequest.programTypeId === ProgramType.TrainingPrograms ||
        programRequest.programTypeId === ProgramType.CraftsAndArtsTrainingPrograms ||
        programRequest.programTypeId === ProgramType.CraftsSpecializationPrograms ||
        programRequest.programTypeId === ProgramType.RefinementPrograms         
      )
    ) {
      return codeBookToSelectData([
        { id: -1, name: t("common.nothingOffered") },
        ...programs,
      ]);
    } else {
      return codeBookToSelectData(programs);
    }
  };

  const handleTypeChange = (event: React.ChangeEvent<SelectEvent>) => {
    const programTypeId = event.target.value as number;
    let newModules = [] as Module[]
    if (programTypeId === ProgramType.TrainingPrograms ||
      programTypeId === ProgramType.CraftsAndArtsTrainingPrograms ||
      programTypeId === ProgramType.CraftsSpecializationPrograms ||
      programTypeId === ProgramType.RefinementPrograms ||      
      programTypeId === ProgramType.NewVocationalCurriculum) {
      newModules.push({ name: "", learningSets: [{ name: "", code: "", csvet: 1, level: 2 }] });
    };

    let newCompetences = [] as Competence[];
    newCompetences.push(createCompetenceInitial());

    setProgramRequest((prev: ProgramRequestData) => ({
      ...prev,
      programId: undefined,
      qualificationTypeId: undefined,
      languageLevelBusinessIds: [],
      languageLevelGeneralIds: [],
      educationSectorId: undefined,
      educationSubSectorId: undefined,
      professionId: undefined,
      programNameFreeEntry: undefined,
      programTypeId,
      hkoLevelId:
        programTypeId === ProgramType.ProgramWithMinisterResolution ? null : 0,
      modules: newModules,
      competences: newCompetences
    }));
  };

  const handleProgramChange = (event: React.ChangeEvent<SelectEvent>) => {
    const programId = event.target.value as number;

    const program = programs?.find((pr: { id: number }) => pr.id === programId);
    const isMinisterProgramType = isMinisterProgramRequest(programRequest.programTypeId);

    if (program) {
      const ministerDetail = isMinisterProgramType ? program.ministerDetail : null;

      setProgramRequest((prev: ProgramRequestData) => ({
        ...prev,
        programId,
        hkoLevelId: isMinisterProgramType ? null : program.hkoLevelId,
        qualificationTypeId: program.qualificationTypeId,
        educationSectorId: program.educationSectorId,
        educationSubSectorId: program.educationSubSectorId,
        ...(ministerDetail ? buildMinisterReadonlyRequestPatch(ministerDetail) : null),
      }));
    }
    else {
      setProgramRequest((prev: ProgramRequestData) => ({
        ...prev,
        programId
      }));
    }
  };

  const handleAdditionalDocumentationRemove = (index: number) => {    
    const newAdditionalDocumentation: AdditionalDocumentation[] = [...programRequest.additionalDocumentation];

    if (index !== -1) {
      newAdditionalDocumentation.splice(index, 1);
      setProgramRequest((prev: ProgramRequestData) => ({
        ...prev,
        additionalDocumentation: newAdditionalDocumentation
      }));
    }
  };

  const handleAdditionalDocumentationDownload = (doc: DocumentData) => {
    if (doc.id === undefined) {
      return;
    }
    if (doc.id === -1) {
      saveAs(doc.file!, doc.fileName);
    }
    else {
      downloadDocument(DocumentType.AdditionalDocumentation, doc.id);
    }

  };
  
  const handleAddAdditionalDocumentationClick = (additionalDocumentationType: AdditionalDocumentationType) => {
    setCurrentAdditionalDocumentationType(additionalDocumentationType);
    setOpenUploadAdditionalDocumentationModal(true);
  };

  const handleCurriculumProgramAdded = async (file: File, event: React.ChangeEvent<HTMLInputElement>) => {
    const curriculumDocument: DocumentData = {
      id: -1,
      fileName: file.name,
      file: file
    };
    setProgramRequest((prev: ProgramRequestData) => ({
      ...prev,
      curriculumProgramDocument: curriculumDocument
    }));
  };

  const handleCurriculumProgramRemoved = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (programRequest.curriculumProgramDocument === undefined || programRequest.curriculumProgramDocument.id === undefined) {
      return;
    }

    setProgramRequest((prev: ProgramRequestData) => ({
      ...prev,
      curriculumProgramDocument: undefined
    }));
  };
  // NISOO 2
  //const handleOtherLegalEntityLocationsChanged = (event: ChangeEvent<HTMLInputElement>) => {
  //  const otherLegalEntityChecked = event.target.checked;

  //  let newOtherLegalEntities: OtherLegalEntityLocation[] = [];
  //  if (otherLegalEntityChecked) {
  //    newOtherLegalEntities = [createOtherLegalEntityLocation()];
  //  }

  //  setProgramRequest((prev) => ({
  //    ...prev,
  //    otherLegalEntityLocations: newOtherLegalEntities,
  //    locationOtherLegalEntity: otherLegalEntityChecked
  //  }));
  //};

  const handleLocationOutsideHeadquartersChanged = (event: React.ChangeEvent<SelectEvent>) => {
    const newLocationOutsideHeadquarters = event.target.value as number[];
    setProgramRequest((prev: ProgramRequestData) => ({
      ...prev,
      locationOutsideHeadquartersIds: newLocationOutsideHeadquarters,
    }));

  }

  const handleEnrollmentConditionsChange = (event: React.ChangeEvent<SelectEvent>) => {
    const newEnrollmentConditions = event.target.value as number[];

    const newOtherEnrollmentConditions = syncOtherArray(
      newEnrollmentConditions,
      programRequest.otherEnrollmentConditions,
      OtherEnrollmentConditionId
    );

    setProgramRequest((prev: ProgramRequestData) => ({
      ...prev,
      enrollmentConditionsIds: newEnrollmentConditions,
      otherEnrollmentConditions: newOtherEnrollmentConditions,
    }));
  }

  const handleKnowledgeChecksChange = (event: React.ChangeEvent<SelectEvent>) => {
    const newKnowledgeChecks = event.target.value as number[];

    const newOtherKnowledgeChecks = syncOtherArray(
      newKnowledgeChecks,
      programRequest.otherKnowledgeChecks,
      OtherKnowledgeCheckId
    );

    setProgramRequest((prev: ProgramRequestData) => ({
      ...prev,
      knowledgeChecksIds: newKnowledgeChecks,
      otherKnowledgeChecks: newOtherKnowledgeChecks,
    }));
  };

  const handleFormsOfTeachingChange = (event: React.ChangeEvent<SelectEvent>) => {
    const newFormsOfTeaching = event.target.value as number[];

    const newOtherForms = syncOtherArray(
      newFormsOfTeaching,
      programRequest.otherFormsOfTeaching,
      OtherFormOfTeachingId
    );
    setProgramRequest((prev: ProgramRequestData) => ({
      ...prev,
      formOfTeachingIds: newFormsOfTeaching,
      otherFormsOfTeaching: newOtherForms
    }));
  };

  const getErrorForPath = (path: string): string | undefined => {
    path = path.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
    path = path.replace(/^\./, '');           // strip a leading dot
    const pathKeys = path.split('.');
    let result: unknown = errors;

    for (const key of pathKeys) {
      if (result && typeof result === "object" && Object.prototype.hasOwnProperty.call(result, key)) {
        if (key.startsWith("competences")) {
          const programTypeId = programRequest.programTypeId;
          if (isProgramTypeWithoutCompetenceSection(programTypeId)) {
            return undefined;
          }
        }
        else if ((key.toLowerCase().startsWith("agencyexpertopiniondocuments") ||
          key.toLowerCase().startsWith("executionresolutiondocuments")) &&
          pathKeys.length === 2 && pathKeys[1] === "issueDate") {
          const programTypeId = programRequest.programTypeId;
          if (isProgramTypeWithoutCompetenceSection(programTypeId)) {
            return undefined;
          }
        }
        result = (result as Record<string, unknown>)[key];
      } else {
        return undefined;
      }
    }
    return typeof result === "string" ? result : undefined;
  }

  const submitProgramRequest = async (sendRequest: boolean) => {
    setIsSendRequest(sendRequest);

    const isMinister = isMinisterProgramRequest(programRequest.programTypeId);
    const hasTeachers = !!programRequest.teachers?.length;

    if (sendRequest && isMinister && !hasTeachers) {
      // Show the existing validation message and prevent opening the confirm modal.
      setChackValidationError(true);
      window.scrollTo(0, 0);
      return;
    }

    const isValid = doValidations();
    if (!isValid) {
      if (chackCompetenceValidation()) {
        setOpenConfirmModal(true);
        return;
      }
      setChackValidationError(true);
      window.scrollTo(0, 0);
      return;
    }
    if (sendRequest) {
      setOpenConfirmAndSendModal(true);
    }
    else {
      setOpenConfirmModal(true);
    }
  }
  // workaround for problem with yup validation, during validation progamTypeId in undwfined
  const chackCompetenceValidation = () => {
    var programTypeId = programRequest.programTypeId;

    if (errors && (programTypeId === ProgramType.BasicEducationPrograms ||
      programTypeId === ProgramType.MiddleSchoolShorterThanThreeYearsPrograms ||
      programTypeId === ProgramType.MiddleSchoolThreeYearsPrograms ||
      programTypeId === ProgramType.MiddleSchoolLongerThanThreeYearsPrograms ||
      programTypeId === ProgramType.ForeignLanguageLearningPrograms ||
      programTypeId === ProgramType.BasicArtsPrograms ||
      programTypeId === ProgramType.MiddleSchoolArtsProgramsLongerThanThreeYears ||
      programTypeId === ProgramType.CroatianLanguageForForeigners)) {
      var properties = Object.getOwnPropertyNames(errors);

      var competences = properties.filter(p => p.startsWith("competences"));

      return (properties.length === competences.length);
    }
    return false;
  }

  const handleCancelConfirmModal = async () => {
    setOpenConfirmModal(false);
  }

  const handleCancelConfirmAndSendModal = async () => {
    setOpenConfirmAndSendModal(false);
  }


  const handleConfirmedSaveModal = async () => runExclusive(async () => {
    setOpenConfirmModal(false);

    await uploadOtherLegalEntityLocationsDocuments();
    await uploadCurriculumProgramDocument();
    await uploadTeachersDocuments();
    await uploadAdditionalDocumentationDocuments();
        
    const request = convertFromProgramRequest(programRequest);

    const isMinister = isMinisterProgramRequest(programRequest.programTypeId);
    if (isMinister && (!programRequest.statusId || programRequest.statusId === ProgramRequestStatus.Opened)) {
      request.newStatus = ProgramRequestStatus.SentMZO;
    }
        
    if (programRequest.statusId === ProgramRequestStatus.Evaluated) {
      request.newStatus = ProgramRequestStatus.SentMZO;
    }

    // For only save, we don't do any status changes.
    if (params.programRequestId && !isNaN(parseInt(params.programRequestId))) {
      editProgramRequest(parseInt(params.programRequestId), request).then(() => {
        setOpenSuccessModal(true);
      });
    }
    else {
      const createdId = await createProgramRequest(request);
      history.replace(programRequestEditUrl(createdId));
      setOpenSuccessModal(true);
    }
  });

  const uploadCurriculumProgramDocument = async () => {
    if (programRequest.curriculumProgramDocument && programRequest.curriculumProgramDocument.id === -1) {      
      const curriculumDocumentRequest = createDocumentRequest(programRequest.curriculumProgramDocument!.file!, DocumentType.CurriculumProgram);
      await uploadDocument(curriculumDocumentRequest).then((idDoc) => {
        var curriculumProgramDocument = programRequest.curriculumProgramDocument;
        curriculumProgramDocument!.id = idDoc;
        setProgramRequest((prev: ProgramRequestData) => ({
          ...prev,
          curriculumProgramDocument: curriculumProgramDocument
        }));
      });
    }
  }

  const uploadOtherLegalEntityLocationsDocuments = async () => {
    if (programRequest.otherLegalEntityLocations && programRequest.otherLegalEntityLocations.length > 0) {
      programRequest.otherLegalEntityLocations.map(async (location: OtherLegalEntityLocation, locationIndex: number) => {
        if (location.programExecutionRequestDocument && location.programExecutionRequestDocument.id === -1) {          
          const programExecutionRequestDocumentRequest = createDocumentRequest(location.programExecutionRequestDocument!.file!, DocumentType.OtherLegalEntityProgramExecutionRequest);
          await uploadDocument(programExecutionRequestDocumentRequest).then((idDoc) => {
            var otherLocationsList = programRequest.otherLegalEntityLocations!.slice();
            if (otherLocationsList && otherLocationsList.length > 0) {
              otherLocationsList[locationIndex]!.programExecutionRequestDocument!.id = idDoc;
              setProgramRequest((prev: ProgramRequestData) => ({
                ...prev,
                otherLegalEntityLocations: otherLocationsList
              }));
            }
          });
        }

        if (location.ministryConsentDocument && location.ministryConsentDocument.id === -1) {          
          const ministryConsentDocumentRequest = createDocumentRequest(location.ministryConsentDocument!.file!, DocumentType.OtherLegalEntityMinistryConsent);
          await uploadDocument(ministryConsentDocumentRequest).then((idDoc) => {
            var otherLocationsList = programRequest.otherLegalEntityLocations!.slice();
            if (otherLocationsList && otherLocationsList.length > 0) {
              otherLocationsList[locationIndex]!.ministryConsentDocument!.id = idDoc;
              setProgramRequest((prev: ProgramRequestData) => ({
                ...prev,
                otherLegalEntityLocations: otherLocationsList
              }));
            }
          });
        }

        if (location.personnelConditionsDocument && location.personnelConditionsDocument.id === -1) {          
          const personnelConditionsDocumentRequest = createDocumentRequest(location.personnelConditionsDocument!.file!, DocumentType.OtherLegalEntityPersonnelConditions);
          await uploadDocument(personnelConditionsDocumentRequest).then((idDoc) => {
            var otherLocationsList = programRequest.otherLegalEntityLocations!.slice();
            if (otherLocationsList && otherLocationsList.length > 0) {
              otherLocationsList[locationIndex]!.personnelConditionsDocument!.id = idDoc;
              setProgramRequest((prev: ProgramRequestData) => ({
                ...prev,
                otherLegalEntityLocations: otherLocationsList
              }));
            }
          });
        }
      });
    }
  }

  const uploadTeachersDocuments = async () => {
    const promises = [] as Promise<void>[];

    if (programRequest.teachers && programRequest.teachers.length > 0) {
      programRequest.teachers!.map((teacher: ProgramRequestTeacher, teacherIndex: number) => {
        if (teacher.diplomaDocument && teacher.diplomaDocument.id === -1) {          
          const diplomaDocumentRequest = createDocumentRequest(teacher.diplomaDocument!.file!, DocumentType.TeacherDiploma);
          const diplomaDocumentPromise = uploadDocument(diplomaDocumentRequest).then((idDoc) => {
            var teachersList = programRequest.teachers!.slice();
            if (teachersList && teachersList.length > 0) {
              teachersList[teacherIndex]!.diplomaDocument!.id = idDoc;
              setProgramRequest((prev: ProgramRequestData) => ({
                ...prev,
                teachers: teachersList
              }));
            }
          });
          promises.push(diplomaDocumentPromise);
        }
        if (teacher.additionalDocumentation && teacher.additionalDocumentation.id === -1) {          
          const additionalDocumentationRequest = createDocumentRequest(teacher.additionalDocumentation!.file!, DocumentType.TeacherAdditional);
          const additionalDocumentationPromise = uploadDocument(additionalDocumentationRequest).then((idDoc) => {
            var teachersList = programRequest.teachers!.slice();
            if (teachersList && teachersList.length > 0) {
              teachersList[teacherIndex]!.additionalDocumentation!.id = idDoc;
              setProgramRequest((prev: ProgramRequestData) => ({
                ...prev,
                teachers: teachersList
              }));
            }
          });
          promises.push(additionalDocumentationPromise);
        }
        if (teacher.statement && teacher.statement.id === -1) {          
          const statementDocumentRequest = createDocumentRequest(teacher.statement!.file!, DocumentType.TeacherStatement);
          const statementDocumentPromise = uploadDocument(statementDocumentRequest).then((idDoc) => {
            var teachersList = programRequest.teachers!.slice();
            if (teachersList && teachersList.length > 0) {
              teachersList[teacherIndex]!.statement!.id = idDoc;
              setProgramRequest((prev: ProgramRequestData) => ({
                ...prev,
                teachers: teachersList
              }));
            }
          });
          promises.push(statementDocumentPromise);
        }

        if (teacher.languages && teacher.languages.length > 0) {
          teacher.languages.map((language: TeacherLanguage, languageIndex: number) => {
            if (language.certificationDocument && language.certificationDocument.id === -1) {
              const languageCertificationRequest = createDocumentRequest(language.certificationDocument!.file!, DocumentType.TeacherLanguageCertification);
              const languageCertificationPromise = uploadDocument(languageCertificationRequest).then((idDoc) => {
                var teachersList = programRequest.teachers!.slice();
                if (teachersList && teachersList.length > 0) {
                  teachersList[teacherIndex]!.languages![languageIndex]!.certificationDocument!.id = idDoc;
                  setProgramRequest((prev: ProgramRequestData) => ({
                    ...prev,
                    teachers: teachersList
                  }));
                }
              });
              promises.push(languageCertificationPromise);
            }

          });
        }

      });
      await Promise.all(promises);
    }
  }


  const uploadAdditionalDocumentationDocuments = async () => {
    const promises = [] as Promise<void>[];

    if (programRequest.additionalDocumentation && programRequest.additionalDocumentation.length > 0) {
      programRequest.additionalDocumentation.map((document: AdditionalDocumentation, documentIndex: number) => {
        if (document && document.additionalDocumentationDocument.id === -1) {          
          const additionalDocumentationDocumentRequest = createDocumentRequest(document.additionalDocumentationDocument.file!, DocumentType.AdditionalDocumentation);
          const additionalDocumentationDocumentPromise = uploadDocument(additionalDocumentationDocumentRequest).then((idDoc) => {
            var additionalDocumentationList = programRequest.additionalDocumentation.slice();
            if (additionalDocumentationList && additionalDocumentationList.length > 0) {
              additionalDocumentationList[documentIndex]!.additionalDocumentationDocument.id = idDoc;
              setProgramRequest((prev: ProgramRequestData) => ({
                ...prev,
                additionalDocumentation: additionalDocumentationList
              }));
            }
          });
          promises.push(additionalDocumentationDocumentPromise)
        }

      });
      await Promise.all(promises);
    }
  };

  const handleConfirmedSaveAndSendModal = async () => runExclusive(async () => {
    setOpenConfirmAndSendModal(false);

    await uploadOtherLegalEntityLocationsDocuments();
    await uploadCurriculumProgramDocument();
    await uploadTeachersDocuments();
    await uploadAdditionalDocumentationDocuments();

    const request = convertFromProgramRequest(programRequest);

    const isMinister = isMinisterProgramRequest(programRequest.programTypeId);
    if (isMinister) {
      request.newStatus = ProgramRequestStatus.SubmittedMZO;
    }
    else {

    // For save and send, do status changes. It shouldn't matter 
    // what status is set. The backend should determine the correct 
    // status.
    if (!programRequest.statusId || programRequest.statusId === ProgramRequestStatus.Opened) {
      request.newStatus = ProgramRequestStatus.SubmittedASOO;
    }
    else if (programRequest.statusId === ProgramRequestStatus.Evaluated || programRequest.statusId === ProgramRequestStatus.SentMZO) {
      request.newStatus = ProgramRequestStatus.SubmittedMZO;
    } else if (programRequest.statusId === ProgramRequestStatus.UnlockedASOO) {
      request.newStatus = ProgramRequestStatus.SubmittedASOO;
    } else if (programRequest.statusId === ProgramRequestStatus.UnlockedMZO) {
      request.newStatus = ProgramRequestStatus.SubmittedMZO;
    } else if (programRequest.statusId === ProgramRequestStatus.HarmonizationUnlockedASOO) {
      request.newStatus = ProgramRequestStatus.HarmonizationASOO;
    }
    }

    if (params.programRequestId && !isNaN(parseInt(params.programRequestId))) {
      editProgramRequest(parseInt(params.programRequestId), request).then(() => {
        setOpenSuccessModal(true);
      });
    }
    else {
      const createdId = await createProgramRequest(request);
      history.replace(programRequestEditUrl(createdId));
      setOpenSuccessModal(true);
    }
  });
    
  const createDocumentRequest = (documentFile: File, documentTypeID: DocumentType): DocumentUploadRequest => {
    return {
      document: documentFile,
      documentTypeId: documentTypeID  
    };
  }

  const handleConfirmedSuccessModal = () => {
    setOpenSuccessModal(false)
    navigateToDashboard();
  }

  const cancelProgramRequest = async () => {
    setOpenCancelModal(true);
  }

  const handleCancelCancelModal = async () => {
    setOpenCancelModal(false);
  }

  const handleConfirmedCancelModal = async () => {
    setOpenCancelModal(false);
    navigateToDashboard();
  }

  const navigateToDashboard = () => {
    history.push(dashboardRoute());
  }

  const handleAdditionalDocumentationConfirm = (documents: DocumentData[]) => {
    if (documents && documents.length > 0) {
      const additionalDocumentationDocuments = programRequest.additionalDocumentation ? programRequest.additionalDocumentation.slice() : [];
      const additionalDocumentation : AdditionalDocumentation[] = documents.map(doc => ({        
        additionalDocumentationTypeId: currentAdditionalDocumentationType!,
        additionalDocumentationDocument: doc
      }));
      additionalDocumentationDocuments.push(...additionalDocumentation);      
      const newProgramRequest: ProgramRequestData = {
        ...programRequest,
        additionalDocumentation: additionalDocumentationDocuments
      };

      setProgramRequest(newProgramRequest);
    }

    setOpenUploadAdditionalDocumentationModal(false);

  };

  const isMinister = isMinisterProgramRequest(programRequest.programTypeId);
  const isNonMinisterUnlockedMzo =
    !isMinister && programRequest.statusId === ProgramRequestStatus.UnlockedMZO;
  const isNonMinisterHarmonizationUnlockedAsoo =
    !isMinister && programRequest.statusId === ProgramRequestStatus.HarmonizationUnlockedASOO;
  const isInstitutionMzomPhase =
    programRequest.statusId === ProgramRequestStatus.Evaluated ||
    programRequest.statusId === ProgramRequestStatus.SentMZO ||
    programRequest.statusId === ProgramRequestStatus.UnlockedMZO;
  const canEditBasicCheck = () => {
    if (
      programRequest.statusId === ProgramRequestStatus.Evaluated ||
      (programRequest.statusId === ProgramRequestStatus.SentMZO &&
        // Minister flow: "Kreiran MZOM" must remain editable for the institution until submit.
        !(isMinister &&
          (userSession?.roleId === RoleEnum.InstitutionAdmin ||
            userSession?.roleId === RoleEnum.InstitutionDirector)))
    ) {
      return false;
    }
    return true;
  };

  const canEditProgramDataSection =
    canEditBasicCheck() && programRequest.statusId !== ProgramRequestStatus.UnlockedMZO;
  // While the institution reworks a harmonization request, the program type and name stay read-only; the rest of the program data can still be edited.
  const canEditProgramTypeAndName =
    canEditProgramDataSection && !isNonMinisterHarmonizationUnlockedAsoo;
  const canEditInstitutionImplementationSection =
    isMinister
      ? canEditBasicCheck()
      : canEditBasicCheck() && !isNonMinisterUnlockedMzo;
  // While the institution reworks a harmonization request, the execution place stays read-only; the rest of the implementation section can still be edited.
  const canEditExecutionPlaceSection =
    canEditInstitutionImplementationSection && !isNonMinisterHarmonizationUnlockedAsoo;

  const canEditRemarkInstitution =
    canEditInstitutionImplementationSection ||
    (!isMinister && isInstitutionMzomPhase);
  const canEditAsooAdditionalDocsSection =
    !isMinister && canEditBasicCheck() && !isNonMinisterUnlockedMzo;
  const canEditCurriculumProgramDocumentSection =
    isMinister
      ? canEditInstitutionImplementationSection
      : canEditProgramDataSection;

  // Program data is read-only for institutions when minister program is selected.
  const isProgramDataReadonly = !canEditProgramDataSection || isMinister;
  const asooAdditionalDocs =
    programRequest.additionalDocumentation?.filter(
      (doc: AdditionalDocumentation) => doc.additionalDocumentationTypeId === AdditionalDocumentationType.AdditionalDocumentationASOO,
    ) ?? [];
  const hasAsooAdditionalDocs = asooAdditionalDocs.length > 0;
  const mzomAdditionalDocs =
    programRequest.additionalDocumentation?.filter(
      (doc: AdditionalDocumentation) => doc.additionalDocumentationTypeId === AdditionalDocumentationType.AdditionalDocumentationMZOM,
    ) ?? [];
  const hasMzomAdditionalDocs = mzomAdditionalDocs.length > 0;
  const uiStatusId = programRequest.statusId ?? ProgramRequestStatus.Opened;
  const showMzomAdditionalDocsSection =
    isMinister ||
    programRequest.statusId === ProgramRequestStatus.Evaluated ||
    programRequest.statusId === ProgramRequestStatus.SentMZO ||
    programRequest.statusId === ProgramRequestStatus.SubmittedMZO ||
    programRequest.statusId === ProgramRequestStatus.UnlockedMZO;
  const canEditMzomAdditionalDocsSection =
    isMinister
      ? canEditBasicCheck() &&
        canEditMinisterMzomAdditionalDocumentation(uiStatusId, userSession?.roleId)
      : (programRequest.statusId !== ProgramRequestStatus.SubmittedMZO &&
        programRequest.statusId !== ProgramRequestStatus.Approved &&
        (canEditBasicCheck() || isInstitutionMzomPhase));
  const shouldShowMzomAdditionalDocsSection =
    showMzomAdditionalDocsSection && (isMinister || canEditMzomAdditionalDocsSection || hasMzomAdditionalDocs);
  const shouldShowTeacherLanguages =
    programRequestDetail?.envelope?.programRequestTypeId ===
    ProgramRequestType.ConsentForExecutionInAForeignLanguage;
  const hasTeachers = !!programRequest.teachers?.length;
  const requiresTeacherForMzoPhase =
    isMinister
      ? (programRequest.statusId === undefined ||
        programRequest.statusId === ProgramRequestStatus.Opened ||
        programRequest.statusId === ProgramRequestStatus.SentMZO ||
        programRequest.statusId === ProgramRequestStatus.SubmittedMZO ||
        programRequest.statusId === ProgramRequestStatus.UnlockedMZO)
      : isInstitutionMzomPhase;
  const isRegularRequestReadyForTeacherSection =
    !isMinister &&
    (programRequest.statusId === ProgramRequestStatus.Evaluated ||
      programRequest.statusId === ProgramRequestStatus.SentMZO ||
      programRequest.statusId === ProgramRequestStatus.SubmittedMZO ||
      programRequest.statusId === ProgramRequestStatus.UnlockedMZO);
  // Institution cannot edit teacher data during ASOO harmonization revision
  const isHarmonizationRevision =
    programRequestDetail?.envelope?.programRequestTypeId === ProgramRequestType.ConsentForHarmonizationASOO &&
    programRequest.statusId === ProgramRequestStatus.HarmonizationUnlockedASOO;
  const canEditTeacherSection =
    !isHarmonizationRevision &&
    (canEditBasicCheck() ||
    (!isMinister && isInstitutionMzomPhase));
  const shouldShowTeacherSection =
    isMinister || hasTeachers || isRegularRequestReadyForTeacherSection;



  useEffect(() => {
    if (canEditTeacherSection && requiresTeacherForMzoPhase && !hasTeachers) {
      setProgramRequest((prev: ProgramRequestData) => ({
        ...prev,
        teachers: [createProgramRequestTeacherInitial(shouldShowTeacherLanguages)],
      }));
    }
  }, [
    canEditTeacherSection,
    hasTeachers,
    requiresTeacherForMzoPhase,
    shouldShowTeacherLanguages,
  ]);

  useEffect(() => {
    if (!programRequest.teachers?.length) {
      return;
    }

    const nextTeachers = programRequest.teachers.map((teacher: ProgramRequestTeacher) => {
      const needsLanguageInitialization =
        shouldShowTeacherLanguages && teacher.languages === undefined;
      const needsMandatoryUpdate =
        teacher.isLanguageMandatory !== shouldShowTeacherLanguages;

      if (!needsLanguageInitialization && !needsMandatoryUpdate) {
        return teacher;
      }

      return {
        ...teacher,
        isLanguageMandatory: shouldShowTeacherLanguages,
        languages: shouldShowTeacherLanguages
          ? (teacher.languages ?? [])
          : teacher.languages,
      };
    });

    if (nextTeachers.some((teacher: ProgramRequestTeacher, index: number) => teacher !== programRequest.teachers[index])) {
      setProgramRequest((prev: ProgramRequestData) => ({
        ...prev,
        teachers: nextTeachers,
      }));
    }
  }, [programRequest.teachers, shouldShowTeacherLanguages]);

  return (
    <>
      <Row>
        <Col md={4}>
          <InstitutionBasicInfo />
        </Col>
        <Col md={8} className={styles.title}>
          <Typography variant="largeBold" component="h2">
            {t("request.title")}
          </Typography>
        </Col>
      </Row>
      <Row>
        <Col md={12} className={"pt-3"}>
          <ExpandablePanel clickableHeader className={styles.panel}>
            <ExpandablePanelHeader>
              {t("request.institutionDataPanelTitle")}
            </ExpandablePanelHeader>
            <ExpandablePanelBody>
              {
                institutionData &&
                <InstitutionPanel institution={institutionData} />
              }
            </ExpandablePanelBody>
          </ExpandablePanel>
        </Col>
      </Row>
      <Row>
        <Col md={12} className={"pt-3"}>
          {chackValidationError && (
            <div className="mb-3">
              <InfoPanel color="warning" message={t("validations.checkRequiredFields")} />
            </div>
          )}
          <ExpandablePanel expanded clickableHeader className={styles.panel}>
            <ExpandablePanelHeader>
              {t("request.programDataPanelTitle")}
            </ExpandablePanelHeader>
            <ExpandablePanelBody className={styles.programPanelBody}>
              <Row>
                <Col md={12} className={styles.rowTitleSpacing}>
                  <Typography variant="smallHeading" component="h3">
                    {t("request.programBasicInfo")}
                  </Typography>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  {!canEditProgramTypeAndName &&
                    <StaticField
                      label={t("common.programType")}
                      value={programTypes.filter((pt: { id: number; name?: string }) => pt.id === programRequest.programTypeId)[0]?.name ?? ""}
                      className={styles.changedField}
                    />
                    ||
                    <Select
                      label={t("request.programType")}
                      name={nameof<typeof programRequest>("programTypeId")}
                      value={programRequest.programTypeId}
                      onChange={handleTypeChange}
                      errorText={errors?.programTypeId}
                      selectData={codeBookToSelectData(programTypes)}
                    />
                  }
                </Col>
                {(programRequest.programTypeId === ProgramType.BasicArtsPrograms
                  || programRequest.programTypeId === ProgramType.MiddleSchoolArtsProgramsLongerThanThreeYears || programRequest.programTypeId === ProgramType.CroatianLanguageForForeigners)
                  ? (
                    <Col md={4}>
                      <TextField
                        label={t("common.programDuration")}
                        name={nameof<typeof programRequest>("guidedLearningDuration")}
                        type="number"
                        value={programRequest.guidedLearningDuration}
                        onChange={handleChange(setProgramRequest)}
                        maxLength={5}
                        min={1}
                        errorText={getErrorForPath("durationChosen") ?? errors?.guidedLearningDuration}
                        readonly={isProgramDataReadonly}
                        plaintext={isProgramDataReadonly}
                      />
                    </Col>
                  ) : (
                    <Col md={4}>
                      <TextField
                        label={t("common.programDuration")}
                        type="number"
                        value={
                          (Number(programRequest.guidedLearningDuration) || 0) +
                          (Number(programRequest.independentLearningDuration) || 0) +
                          (Number(programRequest.workLearningDuration) || 0)
                        }
                        readonly
                        plaintext
                      />
                    </Col>
                  )
                }
                {programRequest.modules && programRequest.modules.length > 0 &&
                  <Col md={4}>
                    <TextField
                      label={t("request.CSVETTotal")}
                      type="number"
                      value={programRequest.modules.reduce((a: number, v: Module) => a + v.learningSets.reduce((learningSetTotal: number, learningSet) => learningSetTotal + (isNaN(learningSet.csvet) ? 0 : learningSet.csvet), 0), 0)}
                      readonly
                      plaintext
                    />
                  </Col>
                }
              </Row>
              {programRequest.programTypeId === ProgramType.ProgramWithMinisterResolution && (
                <Row className={`${styles.rowSpacing} ${styles.infoPanelRowSpacing}`}>
                  <Col md={12}>
                    <InfoPanel color="warning" message={t("request.ministerProgramInfo")} />
                  </Col>
                </Row>
              )}
              {programRequest.programTypeId !== ProgramType.BasicArtsPrograms
                && programRequest.programTypeId !== ProgramType.MiddleSchoolArtsProgramsLongerThanThreeYears && programRequest.programTypeId !== ProgramType.CroatianLanguageForForeigners &&
                <Row>
                  <Col md={4}>
                    <TextField
                      label={t("request.guidedLearningDuration")}
                      name={nameof<typeof programRequest>("guidedLearningDuration")}
                      type="number"
                      value={programRequest.guidedLearningDuration}
                      errorText={getErrorForPath("durationChosen") ?? errors?.guidedLearningDuration}
                      onChange={handleChange(setProgramRequest)}
                      maxLength={5}
                      min={1}
                      readonly={isProgramDataReadonly}
                      plaintext={isProgramDataReadonly}
                    />
                  </Col>
                  <Col md={4}>
                    <TextField
                      label={t("request.workLearningDuration")}
                      name={nameof<typeof programRequest>("workLearningDuration")}
                      type="number"
                      value={programRequest.workLearningDuration}
                      errorText={getErrorForPath("durationChosen") ?? errors?.workLearningDuration}
                      onChange={handleChange(setProgramRequest)}
                      maxLength={5}
                      min={1}
                      readonly={isProgramDataReadonly}
                      plaintext={isProgramDataReadonly}
                    />
                  </Col>
                  <Col md={4}>
                    <TextField
                      label={t("request.independentLearningDuration")}
                      name={nameof<typeof programRequest>("independentLearningDuration")}
                      type="number"
                      value={programRequest.independentLearningDuration}
                      errorText={getErrorForPath("durationChosen") ?? errors?.independentLearningDuration}
                      onChange={handleChange(setProgramRequest)}
                      maxLength={5}
                      min={1}
                      readonly={isProgramDataReadonly}
                      plaintext={isProgramDataReadonly}
                    />
                  </Col>
                </Row>
              }
              <Row>
                <Col md={12}>
                  {!canEditProgramTypeAndName
                    &&
                    <>
                      <StaticField
                        label={t("common.programName")}
                        value={programs?.find((pr: { id: number; name?: string }) => pr.id === programRequest.programId)?.name ?? ""}
                        className={styles.changedField}
                      />
                    </>
                    ||
                    <Autocomplete
                      label={t("common.programName")}
                      name={nameof<typeof programRequest>("programId")}
                      value={programRequest.programId}
                      onChange={handleProgramChange}
                      selectData={getProgramsSelectData()}
                      errorText={errors?.programId}
                      infoText={t("request.programNameInfoMessage")}
                    />
                  }
                </Col>
              </Row>
              {programs && (
                <ConditionalBasicInfo
                  programTypeId={programRequest.programTypeId}
                  programRequestData={programRequest}
                  programRequestDetail={programRequestDetail}
                  programData={programs.find((pr: { id: number }) => pr.id === programRequest.programId)}
                  onChange={handleChange(setProgramRequest)}
                  errors={errors}                  
                  readonly={isProgramDataReadonly}
                />
              )}
              <Row className={styles.rowSpacing}>
                <Col md={4}>
                  {isProgramDataReadonly &&
                    <>
                      <StaticField
                        label={t("request.formOfTeaching")}
                        value={formsOfTeaching?.filter((ft: CodeBook) => programRequest.formOfTeachingIds.includes(ft.id)).map((ft: CodeBook) => ft.name).join(", ") ?? ""}
                        className={styles.changedField}
                      />
                    </>
                    ||
                    <Select
                      multiple
                      label={t("request.formOfTeaching")}
                      name={nameof<typeof programRequest>("formOfTeachingIds")}
                      value={programRequest.formOfTeachingIds}
                      onChange={handleFormsOfTeachingChange}
                      selectData={codeBookToSelectData(formsOfTeaching)}
                      errorText={errors?.formOfTeachingIds?.toString()}
                    />

                  }
                </Col>
                <Col md={4}>
                  {isProgramDataReadonly &&
                    <>
                      <StaticField
                        label={t("request.knowledgeChecks")}
                        value={knowledgeChecks?.filter((kc: CodeBook) => programRequest.knowledgeChecksIds.includes(kc.id)).map((kc: CodeBook) => kc.name).join(", ") ?? ""}
                        className={styles.changedField}
                      />
                    </>
                    ||
                    <Select
                      multiple
                      label={t("request.knowledgeChecks")}
                      name={nameof<typeof programRequest>("knowledgeChecksIds")}
                      value={programRequest.knowledgeChecksIds}
                      onChange={handleKnowledgeChecksChange}
                      selectData={codeBookToSelectData(knowledgeChecks)}
                      errorText={errors?.knowledgeChecksIds?.toString()}
                    />

                  }
                </Col>
                <Col md={4}>
                  {isProgramDataReadonly &&
                    <>
                      <StaticField
                        label={t("request.enrollmentConditions")}
                        value={enrollmentConditions?.filter((ec: CodeBook) => programRequest.enrollmentConditionsIds.includes(ec.id)).map((ec: CodeBook) => ec.name).join(", ") ?? ""}
                        className={styles.changedField}
                      />
                    </>
                    ||
                    <Select
                      multiple
                      label={t("request.enrollmentConditions")}
                      name={nameof<typeof programRequest>("enrollmentConditionsIds")}
                      value={programRequest.enrollmentConditionsIds}
                      onChange={handleEnrollmentConditionsChange}
                      selectData={codeBookToSelectData(enrollmentConditions)}
                      errorText={errors?.enrollmentConditionsIds?.toString()}
                    />
                  }
                </Col>
              </Row>
              {programRequest.formOfTeachingIds.includes(OtherFormOfTeachingId) &&
                ((isProgramDataReadonly &&
                  <StaticField
                    label={t("request.otherFormsOfTeaching")}
                    value={programRequest.otherFormsOfTeaching.toString().replace(/,/g, ", ")}
                  />
                ) || (
                    <OtherData
                      name={nameof<typeof programRequest>("otherFormsOfTeaching")}
                      header={t("request.formOfTeaching")}
                      label={t("request.otherFormsOfTeaching")}
                      addButtonLabel={t("request.addNewFormOfTeaching")}
                      values={programRequest.otherFormsOfTeaching}
                      onChange={handleChange(setProgramRequest)}
                      getErrorForPath={getErrorForPath}
                    />
                  ))
              }

              {programRequest.knowledgeChecksIds.includes(OtherKnowledgeCheckId) &&
                ((isProgramDataReadonly &&
                  <StaticField
                    label={t("request.otherKnowledgeChecks")}
                    value={programRequest.otherKnowledgeChecks.toString().replace(/,/g, ", ")}
                  />
                ) || (
                    <OtherData
                      name={nameof<typeof programRequest>("otherKnowledgeChecks")}
                      header={t("request.knowledgeChecks")}
                      label={t("request.otherKnowledgeChecks")}
                      addButtonLabel={t("request.addNewKnowledgeCheck")}
                      values={programRequest.otherKnowledgeChecks}
                      onChange={handleChange(setProgramRequest)}
                      getErrorForPath={getErrorForPath}
                    />
                  ))
              }



              {programRequest.enrollmentConditionsIds.includes(OtherEnrollmentConditionId) &&
                ((isProgramDataReadonly &&
                  <StaticField
                    label={t("request.otherEnrollmentConditions")}
                    value={programRequest.otherEnrollmentConditions.toString().replace(/,/g, ", ")}
                  />
                ) || (
                    <OtherEnrollmentConditionsInfo
                      name={`${nameof<typeof programRequest>("otherEnrollmentConditions")}`}
                      getErrorForPath={getErrorForPath}
                      otherEnrollmentConditions={programRequest.otherEnrollmentConditions}
                      onChange={handleChange(setProgramRequest)} />
                  ))
              }
              {programRequest.programTypeId !== ProgramType.BasicArtsPrograms
                && programRequest.programTypeId !== ProgramType.MiddleSchoolArtsProgramsLongerThanThreeYears
                && programRequest.programTypeId !== ProgramType.CroatianLanguageForForeigners &&
                <>
                  {isProgramDataReadonly &&
                    <>
                      {programRequest.competences && programRequest.competences.length > 0 &&
                        <>
                          <Row>
                            <Col md={12} className={styles.rowTitleSpacing}>
                              <Typography variant="smallHeading" component="h3">
                                {t("request.competences")}
                              </Typography>
                            </Col>
                          </Row>
                          <Row>
                            <Col md={12}>
                              <ReadOnlyCompetencesTable t={t} competences={programRequest.competences} />
                            </Col>
                          </Row>
                        </>
                      }
                    </>
                    ||
                    <CompetenceInfo
                      name={`${nameof<typeof programRequest>("competences")}`}
                      getErrorForPath={getErrorForPath}
                      competences={programRequest.competences}
                      onChange={handleChange(setProgramRequest)}
                    />
                  }
                </>
              }

              {/* ovisno o statusu ide ili edit ili samo view */}
              {/* za prvu samo edit */}

              {(() => {
                if (!shouldShowTeacherSection) {
                  return null;
                }
                return (
                  <TeacherInfo
                    name={`${nameof<typeof programRequest>("teachers")}`}
                    teachers={programRequest.teachers}
                    readonly={!canEditTeacherSection}
                    showLanguages={shouldShowTeacherLanguages}
                    allowAdd={canEditTeacherSection}
                    onChange={handleChange(setProgramRequest)}
                    getErrorForPath={getErrorForPath}
                    doValidations={doValidations}
                  />
                );
              })()}

              {!isMinister && (canEditAsooAdditionalDocsSection || hasAsooAdditionalDocs) &&
                <Row>
                  <Col md={12} className={styles.rowTitleSpacing}>
                    <Typography variant="smallHeading" component="h3">
                      {t("request.additionalDocumentationASOO")}
                    </Typography>
                  </Col>
                </Row>
              }              
              {!isMinister && hasAsooAdditionalDocs &&
                <Row>
                  <Col md={12}>
                    {asooAdditionalDocs.map((document: AdditionalDocumentation, index: number) => (
                      <DocumentListItem
                        key={index}
                        removable={canEditAsooAdditionalDocsSection}
                        document={document.additionalDocumentationDocument}
                        onRemoveClick={() => handleAdditionalDocumentationRemove(programRequest.additionalDocumentation.indexOf(document))}
                        onDocumentClick={(doc: DocumentData) => handleAdditionalDocumentationDownload(doc)}
                      />
                    ))}
                  </Col>
                </Row>                
              }
              {!isMinister && canEditAsooAdditionalDocsSection &&
                <Row>
                  <Col md={4}>
                    <Button
                      outline
                      color={"primary"}
                      className={styles.uploadButton}
                      icon={<UploadIcon/>}
                      onClick={() => handleAddAdditionalDocumentationClick(AdditionalDocumentationType.AdditionalDocumentationASOO)}
                    >
                      {t("common.addDocument")}
                      {<span className={styles.tooltiptext}>{t("request.additionalDocumentationToolTipASOO")}</span>}
                    </Button>
                  </Col>
                </Row>
              }
              {shouldShowMzomAdditionalDocsSection &&
                <>
                  <Row>
                    <Col md={12} className={styles.rowTitleSpacing}>
                      <Typography variant="smallHeading" component="h3">
                        {t("request.additionalDocumentationMZOM")}
                      </Typography>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      {mzomAdditionalDocs.map((document: AdditionalDocumentation, index: number) => (
                        <DocumentListItem
                          key={index}
                          document={document.additionalDocumentationDocument}
                          removable={canEditMzomAdditionalDocsSection}
                          onRemoveClick={() => handleAdditionalDocumentationRemove(programRequest.additionalDocumentation.indexOf(document))}
                          onDocumentClick={(doc: DocumentData) => handleAdditionalDocumentationDownload(doc)}
                        />
                      ))}
                    </Col>
                  </Row>
                  {canEditMzomAdditionalDocsSection && (
                    <Row>
                      <Col md={4}>
                        <Button
                          outline
                          color={"primary"}
                          className={styles.uploadButton}
                          icon={<UploadIcon />}
                          onClick={() => handleAddAdditionalDocumentationClick(AdditionalDocumentationType.AdditionalDocumentationMZOM)}
                        >
                          {t("common.addDocument")}
                          {<span className={styles.tooltiptext}>{t("request.additionalDocumentationToolTipMZOM")}</span>}
                        </Button>
                      </Col>
                    </Row>
                  )}
                </>
              }

              {(isProgramTypeWithModuleSection(programRequest.programTypeId) || isMinister) &&

                <ProgramRequestModulePanel
                  name={`${nameof<typeof programRequest>("modules")}`}
                  modules={programRequest.modules}
                  programTypeId={programRequest.programTypeId}
                  qualificationTypeId={programRequest.qualificationTypeId ?? 0}
                  getErrorForPath={getErrorForPath}
                  onChange={handleChange(setProgramRequest)}
                  readonly={isProgramDataReadonly}
                />
              }




              <Row>
                <Col md={12} className={styles.rowTitleSpacing}>
                  <Typography variant="smallHeading" component="h3">
                    {t("request.curriculum")}
                  </Typography>
                </Col>
              </Row>
              <Row>
                {!canEditCurriculumProgramDocumentSection &&
                  <Col md={12}>
                    <UploadButton
                      name={nameof<typeof programRequest>("curriculumProgramDocument")}
                      value={programRequest.curriculumProgramDocument}
                      documentType={DocumentType.CurriculumProgram}
                      disabled={true}
                    />
                  </Col>
                  ||
                  <Col md={12}>
                    <UploadButton
                      name={nameof<typeof programRequest>("curriculumProgramDocument")}
                      onFileAdded={handleCurriculumProgramAdded}
                      onFileRemoved={handleCurriculumProgramRemoved}
                      value={programRequest.curriculumProgramDocument}
                      documentType={DocumentType.CurriculumProgram}
                      toolTip={t("document.wordAndPdfTooltip")}
                      fileFilter={t("document.wordAndPdfFilter")}
                      errorText={errors?.curriculumProgramDocument}
                    />
                  </Col>
                }
              </Row>
              {canEditExecutionPlaceSection &&
                <>
                  <Row>
                    <Col md={12} className={styles.rowTitleSpacing}>
                      <Typography variant="smallHeading" component="h3">
                        {t("request.programExecutionPlace")}
                      </Typography>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <Checkbox
                        label={t("request.headquarter")}
                        name={nameof<typeof programRequest>("locationInsideHeadquarters")}
                        value={programRequest.locationInsideHeadquarters}
                        checked={programRequest.locationInsideHeadquarters}
                        errorText={getErrorForPath("locationChosen")}
                        onChange={handleChange(setProgramRequest)}
                      />
                    </Col>
                    <Col md={4}>
                      <Checkbox
                        label={t("request.outsideHeadquarter")}
                        name={nameof<typeof programRequest>("locationOutsideHeadquarters")}
                        value={programRequest.locationOutsideHeadquarters}
                        checked={programRequest.locationOutsideHeadquarters}
                        errorText={getErrorForPath("locationChosen")}
                        onChange={handleChange(setProgramRequest)}
                      />
                    </Col>
                  </Row>
                </>
              }
              {programRequest.locationInsideHeadquarters && institutionData !== undefined && (
                <>
                  <Row>
                    <Col md={12} className={styles.rowTitleSpacing}>
                      <Typography variant="smallHeading" component="h3">
                        {t("request.headquarterExecutionPlaceTitle")}
                      </Typography>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={3}>
                      <TextField
                        label={t("common.address")}
                        plaintext
                        value={institutionData.address}
                      />
                    </Col>
                    <Col md={3}>
                      <TextField
                        label={t("common.town")}
                        plaintext
                        value={institutionData.town.name}
                      />
                    </Col>
                    <Col md={3}>
                      <TextField
                        label={t("common.municipality")}
                        plaintext
                        value={institutionData.town.municipalityName}
                      />
                    </Col>
                    <Col md={3}>
                      <TextField
                        label={t("common.county")}
                        plaintext
                        value={institutionData.town.countyName}
                      />
                    </Col>
                  </Row>
                </>
              )}
              {programRequest.locationOutsideHeadquarters && institutionData !== undefined && (
                <>
                  <Row>
                    <Col md={12} className={styles.rowTitleSpacing}>
                      <Typography variant="smallHeading" component="h3">
                        {t("request.outsideHeadquarterExecutionPlaceTitle")}
                      </Typography>
                    </Col>
                  </Row>
                  {canEditExecutionPlaceSection &&
                    <Row className={styles.outsideHeadquarterSelectRow}>
                      <Col md={6}>
                        <Select
                          multiple
                          label=""
                          name={nameof<typeof programRequest>("locationOutsideHeadquartersIds")}
                          value={programRequest.locationOutsideHeadquartersIds}
                          onChange={handleChange(setProgramRequest)}
                          selectData={fillLocations() || []}
                          errorText={errors?.locationOutsideHeadquartersIds?.toString()}
                        />
                      </Col>
                    </Row>
                  }

                  {programRequest.locationOutsideHeadquartersIds !=
                    null &&
                    institutionData?.locations
                      .filter((x: InstitutionLocation) => programRequest.locationOutsideHeadquartersIds.includes(x.id))
                      .map(
                        (institutionLocation: InstitutionLocation) => {
                          if (institutionLocation) {
                            return (
                              <Row>
                                <Col md={3}>
                                  <TextField
                                    label={t("request.addressOutsideHeadquarter")}
                                    plaintext
                                    value={institutionLocation.address}
                                  />
                                </Col>
                                <Col md={3}>
                                  <TextField
                                    label={t("request.municipalityOutsideHeadquarter")}
                                    plaintext
                                    value={(institutionLocation.town as { name?: string }).name ?? institutionLocation.town.municipalityName}
                                  />
                                </Col>
                                <Col md={3}>
                                  <TextField
                                    label={t("request.townOutsideHeadquarter")}
                                    plaintext
                                    value={institutionLocation.town.municipalityName}
                                  />
                                </Col>
                                <Col md={3}>
                                  <TextField
                                    label={t("request.countyOutsideHeadquarter")}
                                    plaintext
                                    value={institutionLocation.town.countyName}
                                  />
                                </Col>
                              </Row>
                            );
                          }
                          else {
                            return <></>
                          }
                        }
                      )}
                </>
              )}
              {programRequest.locationOtherLegalEntity && (
                <OtherLegalEntityInfos
                  name={`${nameof<typeof programRequest>("otherLegalEntityLocations")}`}
                  getErrorForPath={getErrorForPath}
                  otherLegalEntityLocations={programRequest.otherLegalEntityLocations}
                  onChange={handleChange(setProgramRequest)} />
              )}

              <Row>
                <Col md={12} className={styles.rowTitleSpacing}>
                  <Typography variant="smallHeading" component="h3">
                    {t("common.note")}
                  </Typography>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  {canEditRemarkInstitution ? (
                    <TextArea
                      label={""}
                      name={nameof<typeof programRequest>("remarkInstitution")}
                      value={programRequest.remarkInstitution}
                      onChange={handleChange(setProgramRequest)}
                      ariaLabel={t("common.note")}
                      maxLength={700}
                      rows={4}
                      errorText={errors?.remarkInstitution}
                    />
                  ) : (
                    <StaticField
                      label={""}
                      value={programRequest.remarkInstitution}
                    />
                  )}
                </Col>
              </Row>
            </ExpandablePanelBody>
          </ExpandablePanel>
        </Col>
      </Row>
      <Row style={{ marginLeft: "-15px", marginRight: "-15px" }} className={"pt-2"}>
        <Col md={3}>
          <Button
            outline
            onClick={cancelProgramRequest}
          >
            {t("common.cancel")}
          </Button>
        </Col>
        <Col md={9} className={styles.addDataButtonCol}>
          <Button
            onClick={() => submitProgramRequest(false)}
            disabled={isSaving || !institutionData || !institutionData.andragogicLeaders || institutionData.andragogicLeaders.length === 0}
            className={styles.saveButton}
          >
            {t("common.save")}
          </Button>
          <Button
            onClick={() => submitProgramRequest(true)}
            disabled={isSaving || !institutionData || !institutionData.andragogicLeaders || institutionData.andragogicLeaders.length === 0}
          >
            {t("request.submitRequest")}
          </Button>
        </Col>
      </Row>
      <Modal
        type="confirm"
        open={openConfirmModal}
        cancelText={t("common.cancel")}
        confirmText={t("common.yesSaveChanges")}
        onClose={handleCancelConfirmModal}
        onConfirm={handleConfirmedSaveModal}
      >
        <div>

          <Typography component="h1" variant="largeBold">
            {t("request.areYouSureRequestSave")}
          </Typography>
          {t("request.dataConfirmationSubmission")}
        </div>
      </Modal>
      <Modal
        type="confirm"
        open={openConfirmAndSendModal}
        cancelText={t("common.cancel")}
        confirmText={t("request.confirmRequestSubmission")}
        onClose={handleCancelConfirmAndSendModal}
        onConfirm={handleConfirmedSaveAndSendModal}
      >
        <div>

          <Typography component="h1" variant="largeBold">
            {t("request.areYouSureRequestSubmission")}
          </Typography>
          {t("request.dataValidityConfirmationRequestSubmission")}
        </div>
      </Modal>
      <Modal
        type="message"
        open={openSuccessModal}
        confirmText={t("common.close")}
        onClose={handleConfirmedSuccessModal}
        onConfirm={handleConfirmedSuccessModal}
      >
        <div className={styles.modalMessageContentAlignCenter}>

          <Typography component="h1" variant="largeBold">
            {isSendRequest ? t("request.requestSuccessfullySubmittedAndSend") : t("request.requestSuccessfullySubmitted")}
          </Typography>
        </div>
      </Modal>
      <Modal
        type="confirm"
        open={openCancelModal}
        cancelText={t("common.noProperCase")}
        confirmText={t("common.yesCancel")}
        onClose={handleCancelCancelModal}
        onConfirm={handleConfirmedCancelModal}
      >
        <div>

          <Typography component="h1" variant="largeBold">
            {t("common.areYouSureCancelChanges")}
          </Typography>
          {t("common.areYouSureCancelChangesNote")}
        </div>
      </Modal>
      <DocumentsUploadModal        
        title={currentAdditionalDocumentationType === AdditionalDocumentationType.AdditionalDocumentationASOO ? t("request.additionalDocumentationASOO") : t("request.additionalDocumentationMZOM")}        
        infoPanelText={currentAdditionalDocumentationType === AdditionalDocumentationType.AdditionalDocumentationASOO ? t("request.additionalDocumentationPanelASOO") : t("request.additionalDocumentationPanelMZOM")}
        types={t("document.imagesAndPdfAndWordFilter")}
        allDocuments={programRequest.additionalDocumentation.filter((doc: AdditionalDocumentation) => doc.additionalDocumentationTypeId === currentAdditionalDocumentationType).map((doc: AdditionalDocumentation) => doc.additionalDocumentationDocument)}
        onRemoveUploadedDocument={(filteredIndex: number) => {
          const docsOfType = programRequest.additionalDocumentation.filter(
            (doc: AdditionalDocumentation) => doc.additionalDocumentationTypeId === currentAdditionalDocumentationType
          );
          const target = docsOfType[filteredIndex];
          handleAdditionalDocumentationRemove(target ? programRequest.additionalDocumentation.indexOf(target) : -1);
        }}
        maxSize={10}        
        maxFiles={programRequest.additionalDocumentation ? 10 - programRequest.additionalDocumentation.filter((doc: AdditionalDocumentation) => doc.additionalDocumentationTypeId === currentAdditionalDocumentationType).length : 10}
        filesAction={undefined}
        open={openUploadAdditionalDocumentationModal}
        onConfirm={handleAdditionalDocumentationConfirm}
        onClose={() => setOpenUploadAdditionalDocumentationModal(false)}
        hideOldDocuments={false}
        btnCancelText={t("common.cancel")}
        btnOkText={t("common.addNew")}
      />
    </>
  );
}
